/**
 * Per-lane concatenation of `UnifiedFilters` — the single implementation
 * shared by graph-service's `composeRuntimeFilters` (request-time envelope
 * composition) and design-system's `composeLevelFilters` (config-layer
 * composition). Two implementations of a security invariant (never-widen) is
 * the highest-risk kind of duplication, so this lives once, here.
 *
 * `UnifiedFilters` has no top-level logic field — every lane is conjoined by
 * the query engines, so concatenating two layers' lanes can only narrow the
 * result set (monotonic — the composed result's per-lane length is always
 * the sum of the contributing lanes' lengths).
 */
import type { UnifiedFilters } from 'types/filters';

function concatLane<T>(a: T[] | undefined, b: T[] | undefined): T[] | undefined {
  if (!a?.length && !b?.length) return undefined;
  return [...(a ?? []), ...(b ?? [])];
}

/**
 * Concatenates two `UnifiedFilters` lane-by-lane. Returns `undefined` when
 * neither side contributes anything, so an unfiltered level's query stays
 * byte-identical to before composition existed.
 */
export function concatUnifiedFilters(
  a: UnifiedFilters | undefined,
  b: UnifiedFilters | undefined
): UnifiedFilters | undefined {
  if (!a && !b) return undefined;

  const propertyFilters = concatLane(a?.propertyFilters, b?.propertyFilters);
  const perspectiveFilters = concatLane(a?.perspectiveFilters, b?.perspectiveFilters);
  const relationshipFilters = concatLane(a?.relationshipFilters, b?.relationshipFilters);
  const arcFilters = concatLane(a?.arcFilters, b?.arcFilters);
  const filterGroups = concatLane(a?.filterGroups, b?.filterGroups);

  if (!propertyFilters && !perspectiveFilters && !relationshipFilters && !arcFilters && !filterGroups) {
    return undefined;
  }

  return { propertyFilters, perspectiveFilters, relationshipFilters, arcFilters, filterGroups };
}

/** True when a `UnifiedFilters` has at least one non-empty lane. */
export function hasAnyFilterLane(filters: UnifiedFilters | undefined): boolean {
  if (!filters) return false;
  return Boolean(
    filters.propertyFilters?.length ||
      filters.perspectiveFilters?.length ||
      filters.relationshipFilters?.length ||
      filters.arcFilters?.length ||
      filters.filterGroups?.length
  );
}
